import React, { useEffect, useState } from 'react';
import { usePortalStore } from '../../store';
import { ArrowLeft, Edit, Zap, Activity, Clock } from 'lucide-react';

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3000';

interface ActiveWorkflow {
  epic_id: string;
  epic_title: string;
  task_id: string;
  current_place: string;
  last_transition: string;
  status: string;
  quality_colour: string;
  time_in_state_seconds: number | null;
}

const TIER_CLASSES: Record<string, string> = {
  gray: 'bg-slate-700 text-slate-300',
  green: 'bg-emerald-900 text-emerald-200',
  blue: 'bg-blue-900 text-blue-200',
  purple: 'bg-purple-900 text-purple-200',
};

function fmtDuration(s: number | null): string {
  if (s === null) return '—';
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h`;
}

function ActiveWorkflowsSection() {
  const [workflows, setWorkflows] = useState<ActiveWorkflow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/workflow-monitor/active`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: ActiveWorkflow[]) => setWorkflows(data.slice(0, 5)))
      .catch(() => setWorkflows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Activity className="w-4 h-4 text-cyan-400" />
        <h2 className="text-lg font-semibold text-slate-100">Active Workflows</h2>
      </div>
      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : workflows.length === 0 ? (
        <p className="text-slate-500 text-sm">No workflows currently in progress.</p>
      ) : (
        <div className="space-y-3">
          {workflows.map(w => {
            const cls = TIER_CLASSES[w.quality_colour] ?? TIER_CLASSES.gray;
            return (
              <div key={`${w.epic_id}-${w.task_id}`} className="bg-slate-900 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="text-sm font-medium text-slate-100 truncate flex-1">
                    {w.epic_title}
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium flex-shrink-0 ${cls}`}>
                    {w.quality_colour}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Zap className="w-3 h-3 text-cyan-400" />
                  <span>{w.last_transition}</span>
                  <span className="text-slate-600">·</span>
                  <span>{w.current_place}</span>
                  <span className="text-slate-600">·</span>
                  <Clock className="w-3 h-3" />
                  <span>{fmtDuration(w.time_in_state_seconds)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const DepartmentView: React.FC = () => {
  const { selectedDepartment, navigateTo, clearDepartmentSelection } = usePortalStore();

  if (!selectedDepartment) {
    return (
      <div className="text-slate-400 text-center py-12">
        <p>No department selected. Please select one from the overview.</p>
        <button
          onClick={() => navigateTo('company-overview')}
          className="mt-4 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
        >
          Back to Overview
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <button
          onClick={() => {
            clearDepartmentSelection();
            navigateTo('company-overview');
          }}
          className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-100">{selectedDepartment.name} Department</h1>
          <p className="text-slate-400 mt-1">{selectedDepartment.charter}</p>
        </div>
        <button className="ml-auto p-2 hover:bg-slate-700 rounded-lg transition-colors">
          <Edit className="w-5 h-5 text-slate-400" />
        </button>
      </div>

      {/* Skills Grid */}
      <div>
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Capabilities (Skills)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {selectedDepartment.skills.map(skill => (
            <div key={skill.id} className="bg-slate-800 border border-slate-700 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-100">{skill.name}</h3>
                  <div className="flex gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map(i => (
                      <div
                        key={i}
                        className={`w-3 h-3 rounded-full ${
                          i <= Math.floor(skill.reputation) ? 'bg-yellow-500' : 'bg-slate-700'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div
                  className={`px-2 py-1 rounded text-xs font-bold ${
                    skill.status === 'active'
                      ? 'bg-emerald-900 text-emerald-200'
                      : skill.status === 'idle'
                      ? 'bg-slate-700 text-slate-300'
                      : 'bg-red-900 text-red-200'
                  }`}
                >
                  {skill.status.toUpperCase()}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Queue</span>
                  <span className="font-semibold">{skill.queueDepth}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Speed</span>
                  <span className="font-semibold">{(skill.speed * 100).toFixed(0)}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Error Rate</span>
                  <span className={skill.errorRate > 0.05 ? 'text-red-400' : 'text-emerald-400'}>
                    {(skill.errorRate * 100).toFixed(1)}%
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Cost/Exec</span>
                  <span className="font-semibold">{skill.costPerExecution} tokens</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Active Workflows — live from petri_net_state.db */}
      <ActiveWorkflowsSection />

      {/* Department Docs */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-4">Department Documents</h2>
        <div className="flex flex-wrap gap-2">
          {['Charter', 'Agents', 'Context', 'Policies', 'Processes'].map(doc => (
            <button
              key={doc}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-700 rounded-lg text-sm transition-colors text-slate-300"
            >
              {doc}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
